import React, { useMemo, useState } from 'react';
import { IContentTypeEntries } from '@constants/metas/default';

import { CMS_PAGE_SECTIONS } from '@enums/contentStack';
import useContentStackLivepreview from '@hooks/use-contentstack-livepreview';
import Spinner from '@components/atoms/spinner/spinner';
import CMSContentContainer from './cms-content-container';

interface CMSPageContainerProps {
  contentTypeEntries: IContentTypeEntries;
  isLivePreview?: boolean;
  elementStartingPosition?: number | undefined;
}

const CMSPageContainer: React.FC<CMSPageContainerProps> = ({
  contentTypeEntries,
  isLivePreview,
}): React.JSX.Element => {
  const [contentStackEntry, setContentStackEntry] = useState(contentTypeEntries);
  const { isLivePreviewModeEnabled, hasLivePreviewError } = useContentStackLivepreview(setContentStackEntry);

  const cmsPageContent = useMemo(() => {
    if (!contentStackEntry) {
      return null;
    }

    const pageType = contentStackEntry?.contentTypeId;
    const sections = CMS_PAGE_SECTIONS(isLivePreviewModeEnabled)?.[pageType];
    if (!sections?.length) {
      if (isLivePreview) {
        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '60vh',
              gap: '16px',
            }}
          >
            {hasLivePreviewError ? (
              <p
                role="alert"
                style={{ color: 'red' }}
              >
                Failed to load preview. Please try again.
              </p>
            ) : (
              <>
                <Spinner />
                <p>Loading preview</p>
              </>
            )}
          </div>
        );
      }
      return null;
    }

    return (
      <div
        data-pageref={contentStackEntry?.id || contentStackEntry?.uid}
        data-contenttype={contentStackEntry?.contentTypeId}
        data-locale={`${contentStackEntry?.locale}`}
      >
        <CMSContentContainer
          contentTypeEntry={contentStackEntry}
          {...{ sections }}
        />
      </div>
    );
  }, [isLivePreviewModeEnabled, contentStackEntry, isLivePreview, hasLivePreviewError]);

  return <>{cmsPageContent}</>;
};

export default CMSPageContainer;
